// Charts in lanes (view model core/screens/lanes.ts), drawn in pixels at the width they get: the words keep their size
// on every phone instead of growing with a viewBox. Prototype "Ein Bezug" and "Bezug läuft": a short line and the
// name with its unit at each lane's top left, words at the curves, the time below, no scales. Where the view can be
// read at one moment, a finger (or the arrow keys) moves a cursor and the header shows the values there.
import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  fitLanes,
  type Lane,
  type LaneBand,
  type LaneChartView,
  type LanePoint,
  type LaneSeries,
  type ScrubReading,
} from '../../core/index.js';
import { InfoTip } from './Info.js';

const LEFT = 8;
/** the legend strip above each plot */
const LEGEND = 24;
const LANE_GAP = 4;
const TICKS = 24;
/** Open Sans 12/600: an estimate of a label's width, for placing the next legend entry */
const charW = (text: string, size = 12) => text.length * size * 0.56;

/** The width the chart gets (its card's inner width), measured; 334 px before the first measurement. */
function useWidth<T extends Element>(initial: number): [RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(initial);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    set();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

interface Geometry {
  x: (v: number) => number;
  right: number;
  lanes: { top: number; bottom: number; y0: number }[];
  height: number;
}

function geometry(view: LaneChartView, width: number): Geometry {
  const right = Math.max(LEFT + 40, width - view.gutter);
  const span = view.x.to - view.x.from || 1;
  const x = (v: number) => LEFT + ((v - view.x.from) / span) * (right - LEFT);
  const lanes: Geometry['lanes'] = [];
  let y0 = 0;
  for (const lane of view.lanes) {
    const top = y0 + LEGEND;
    const bottom = top + lane.height;
    lanes.push({ top, bottom, y0 });
    y0 = bottom + LANE_GAP;
  }
  const last = lanes.at(-1)?.bottom ?? 0;
  return { x, right, lanes, height: last + (view.x.ticks.length ? TICKS : 4) };
}

const yOf = (v: number, lo: number, hi: number, top: number, bottom: number) =>
  bottom - ((Math.min(hi, Math.max(lo, v)) - lo) / (hi - lo || 1)) * (bottom - top);

const r1 = (v: number) => Math.round(v * 10) / 10;

/** A line through the points; a gap (null) breaks it. */
function linePath(points: readonly LanePoint[], x: (v: number) => number, y: (v: number) => number): string {
  let out = '';
  let on = false;
  for (const [px, v] of points) {
    if (v === null) {
      on = false;
      continue;
    }
    out += `${on ? 'L' : 'M'}${r1(x(px))} ${r1(y(v))}`;
    on = true;
  }
  return out;
}

/**
 * The same line as a flowing curve: monotone cubic segments (Fritsch–Carlson) through the points, so it never swings
 * past a measured value; points closer than 2 px are merged first (a 20-minute history has a few per pixel).
 */
function curvePath(points: readonly LanePoint[], x: (v: number) => number, y: (v: number) => number): string {
  let out = '';
  let run: [number, number][] = [];
  const flush = () => {
    out += curveRun(run);
    run = [];
  };
  points.forEach(([px, v], i) => {
    if (v === null) {
      flush();
      return;
    }
    const sx = x(px);
    const last = run.at(-1);
    const isEnd = i === points.length - 1 || (points[i + 1] as LanePoint)[1] === null;
    if (last && sx - last[0] < 2 && !isEnd) return;
    if (last && sx - last[0] < 2 && run.length > 1) run.pop();
    run.push([sx, y(v)]);
  });
  flush();
  return out;
}

function curveRun(p: readonly [number, number][]): string {
  const n = p.length;
  if (n === 0) return '';
  const at = (i: number) => p[i] as [number, number];
  if (n < 3) return p.map(([px, py], i) => `${i ? 'L' : 'M'}${r1(px)} ${r1(py)}`).join('');
  const dx: number[] = [];
  const slope: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(at(i + 1)[0] - at(i)[0]);
    slope.push(dx[i] ? (at(i + 1)[1] - at(i)[1]) / (dx[i] as number) : 0);
  }
  const m: number[] = [slope[0] as number];
  for (let i = 1; i < n - 1; i++) {
    const a = slope[i - 1] as number;
    const b = slope[i] as number;
    m.push(a * b <= 0 ? 0 : (a + b) / 2);
  }
  m.push(slope[n - 2] as number);
  for (let i = 0; i < n - 1; i++) {
    const s = slope[i] as number;
    if (s === 0) {
      m[i] = 0;
      m[i + 1] = 0;
      continue;
    }
    const a = (m[i] as number) / s;
    const b = (m[i + 1] as number) / s;
    const h = a * a + b * b;
    if (h > 9) {
      const t = 3 / Math.sqrt(h);
      m[i] = t * a * s;
      m[i + 1] = t * b * s;
    }
  }
  let d = `M${r1(at(0)[0])} ${r1(at(0)[1])}`;
  for (let i = 0; i < n - 1; i++) {
    const h = (dx[i] as number) / 3;
    const [x0, y0] = at(i);
    const [x1, y1] = at(i + 1);
    d += `C${r1(x0 + h)} ${r1(y0 + (m[i] as number) * h)} ${r1(x1 - h)} ${r1(y1 - (m[i + 1] as number) * h)} ${r1(x1)} ${r1(y1)}`;
  }
  return d;
}

function lastPoint(points: readonly LanePoint[]): [number, number] | null {
  for (let i = points.length - 1; i >= 0; i--) {
    const p = points[i] as LanePoint;
    if (p[1] !== null) return [p[0], p[1]];
  }
  return null;
}

function extreme(points: readonly LanePoint[], most: boolean): [number, number] | null {
  let best: [number, number] | null = null;
  for (const [px, v] of points)
    if (v !== null && (best === null || (most ? v > best[1] : v < best[1]))) best = [px, v];
  return best;
}

interface Placed {
  key: string;
  x: number;
  y: number;
  text: string;
  anchor: 'start' | 'middle' | 'end';
  className: string;
}

/** The words of one lane's series, the end values pushed apart so two never sit on each other. */
function seriesWords(lane: Lane, g: Geometry, box: { top: number; bottom: number }, width: number): Placed[] {
  const out: Placed[] = [];
  // the end values of a lane stand together after the curve that runs furthest: at the plot's right edge for a whole
  // shot, at the curve's end while it is still running (board "Bezug läuft": "1,8 g/s" and "19,9 g" right at the
  // lines; 23.10.2026 they stood at the right edge, far behind the curves)
  const ends = lane.series
    .filter((s) => !s.faint && (s.marks ?? []).some((m) => m.at === 'end'))
    .map((s) => lastPoint(s.points)?.[0])
    .filter((x): x is number => x !== undefined);
  const endX = ends.length ? Math.min(g.right, Math.max(...ends.map((x) => g.x(x)))) : g.right;
  for (const s of lane.series) {
    if (s.faint) continue;
    const y = (v: number) => yOf(v, s.lo, s.hi, box.top, box.bottom);
    for (const m of s.marks ?? []) {
      const p = m.at === 'end' ? lastPoint(s.points) : extreme(s.points, m.at === 'max');
      if (!p) continue;
      const cls = `lane-val tone-${s.tone}${m.strong ? '' : ' is-weak'}`;
      if (m.at === 'end')
        out.push({
          key: `${s.key}-end`,
          x: endX + 7,
          y: y(p[1]) + 4,
          text: m.text,
          anchor: 'start',
          className: cls,
        });
      else {
        const w = charW(m.text, 13);
        const cx = Math.min(width - w / 2, Math.max(LEFT + w / 2, g.x(p[0])));
        const cy =
          m.at === 'max' ? Math.max(box.top - 4, y(p[1]) - 7) : Math.min(box.bottom - 2, y(p[1]) + 15);
        out.push({ key: `${s.key}-${m.at}`, x: cx, y: cy, text: m.text, anchor: 'middle', className: cls });
      }
    }
  }
  const placed = out.filter((p) => p.anchor === 'start').sort((a, b) => a.y - b.y);
  for (let i = 1; i < placed.length; i++) {
    const a = placed[i - 1] as Placed;
    const b = placed[i] as Placed;
    if (b.y - a.y < 15) b.y = a.y + 15;
  }
  return out;
}

interface Rect {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** The distance between two boxes, 0 when they touch. */
const apart = (a: Rect, b: Rect) =>
  Math.hypot(
    Math.max(0, a.left - b.right, b.left - a.right),
    Math.max(0, a.top - b.bottom, b.top - a.bottom),
  );

/** The box of a word drawn at (x, y) with its anchor. */
function wordRect(text: string, x: number, y: number, anchor: Placed['anchor'], size = 12): Rect {
  const w = charW(text, size);
  const left = anchor === 'start' ? x : anchor === 'end' ? x - w : x - w / 2;
  return { left, right: left + w, top: y - size, bottom: y + 3 };
}

/** A line as points every 2 px. */
function along(points: readonly LanePoint[], g: Geometry, y: (v: number) => number): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 1; i < points.length; i++) {
    const [x0, v0] = points[i - 1] as LanePoint;
    const [x1, v1] = points[i] as LanePoint;
    if (v0 === null || v1 === null) continue;
    const a = g.x(x0);
    const b = g.x(x1);
    const n = Math.max(1, Math.ceil((b - a) / 2));
    for (let k = 0; k <= n; k++) out.push([a + ((b - a) * k) / n, y(v0 + ((v1 - v0) * k) / n)]);
  }
  return out;
}

/**
 * Where the band's name stands: above the band at `labelAt`, ending there, as on the prototype, unless a curve or a word
 * of the lane comes closer than 8 px (a shot quicker than the usual ones runs right above the band, Dominik's shot of
 * 23.10.2026); then the spot along the band nearest to it, above (ending at the spot) or below (starting there, where
 * the rising band leaves room), that keeps 8 px from every line and word and does not touch the band; else the freest.
 */
function bandLabelSpot(
  lane: Lane,
  band: LaneBand,
  g: Geometry,
  box: { top: number; bottom: number },
  words: Rect[],
): { x: number; y: number; anchor: 'start' | 'end' } {
  const yB = (v: number) => yOf(v, band.lo, band.hi, box.top, box.bottom);
  const lines = lane.series.flatMap((s) =>
    along(s.points, g, (v) => yOf(v, s.lo, s.hi, box.top, box.bottom)),
  );
  const edges = [...along(band.high, g, yB), ...along(band.low, g, yB)];
  const across = (y: number): Rect => ({ left: LEFT, right: g.right, top: y, bottom: y });
  const others: Rect[] = [
    ...words,
    across(box.bottom),
    ...lane.rules.map((r) => across(yOf(r.v, r.lo, r.hi, box.top, box.bottom))),
    ...lane.marks.map((m) => ({ left: g.x(m.x), right: g.x(m.x), top: box.top, bottom: box.bottom })),
  ];
  const dot = ([x, y]: [number, number]): Rect => ({ left: x, right: x, top: y, bottom: y });
  const clearance = (r: Rect) =>
    Math.min(Infinity, ...lines.map((p) => apart(r, dot(p))), ...others.map((o) => apart(r, o)));
  const free = (r: Rect) =>
    r.left >= LEFT && r.right <= g.right && r.top >= box.top && edges.every((p) => apart(r, dot(p)) > 0);
  const near = band.high.reduce((a, p) =>
    Math.abs(p[0] - band.labelAt) < Math.abs(a[0] - band.labelAt) ? p : a,
  );
  const first = { x: g.x(band.labelAt), y: yB(near[1] ?? 0) - 7, anchor: 'end' as const };
  if (clearance(wordRect(band.label, first.x, first.y, 'end')) >= 8) return first;
  const spots: { x: number; y: number; anchor: 'start' | 'end'; c: number }[] = [];
  band.high.forEach(([px, hi], i) => {
    const lo = band.low[i]?.[1];
    const x = g.x(px);
    const ways = [
      ...(hi !== null ? [{ x, y: yB(hi) - 7, anchor: 'end' as const }] : []),
      ...(lo !== null && lo !== undefined ? [{ x, y: yB(lo) + 15, anchor: 'start' as const }] : []),
    ];
    for (const w of ways) {
      const r = wordRect(band.label, w.x, w.y, w.anchor);
      if (free(r)) spots.push({ ...w, c: clearance(r) });
    }
  });
  const ok = spots.filter((s) => s.c >= 8).sort((a, b) => Math.abs(a.x - first.x) - Math.abs(b.x - first.x));
  const best = ok[0] ?? spots.sort((a, b) => b.c - a.c)[0];
  return best ?? first;
}

function SeriesLine({ s, g, box }: { s: LaneSeries; g: Geometry; box: { top: number; bottom: number } }) {
  const y = (v: number) => yOf(v, s.lo, s.hi, box.top, box.bottom);
  const d = curvePath(s.points, g.x, y);
  if (!d) return null;
  const end = s.dot ? lastPoint(s.points) : null;
  const first = s.points.find((p) => p[1] !== null);
  const lastX = lastPoint(s.points)?.[0];
  return (
    <>
      {s.area && first && lastX !== undefined && (
        <path
          className="lane-area"
          d={`${d}L${r1(g.x(lastX))} ${box.bottom}L${r1(g.x(first[0]))} ${box.bottom}Z`}
        />
      )}
      <path
        className={`lane-line tone-${s.tone}${s.dashed ? ' is-dashed' : ''}${s.dotted ? ' is-dotted' : ''}${s.faint ? ' is-faint' : ''}`}
        d={d}
        strokeWidth={s.width}
      />
      {end && (
        <circle
          className={`lane-dot tone-${s.tone}`}
          cx={r1(g.x(end[0]))}
          cy={r1(y(end[1]))}
          r={s.key === 'flow' ? 3.5 : 4}
        />
      )}
    </>
  );
}

function LaneBody({
  lane,
  g,
  index,
  width,
  avoid,
}: {
  lane: Lane;
  g: Geometry;
  index: number;
  width: number;
  /** words of the whole chart in this lane (the pre-infusion's name) */
  avoid: Rect[];
}) {
  const box = g.lanes[index] as Geometry['lanes'][number];
  let lx = LEFT;
  const legend = lane.legend.map((l) => {
    const at = lx;
    const text = l.plain ? at : at + 20;
    lx = text + charW(l.label) + 18;
    return (
      <g key={l.label}>
        {!l.plain && (
          <line
            className={`lane-legend-line tone-${l.tone}${l.dashed ? ' is-dashed' : ''}${l.dotted ? ' is-dotted' : ''}`}
            x1={at}
            x2={at + 14}
            y1={box.y0 + 10}
            y2={box.y0 + 10}
          />
        )}
        <text className={`lane-legend tone-${l.tone}`} x={text} y={box.y0 + 14}>
          {l.label}
        </text>
      </g>
    );
  });
  const words = seriesWords(lane, g, box, width);
  const bars = lane.bars;
  const slot = (g.x(1) - g.x(0)) * 0.46;
  return (
    <g data-lane={lane.key}>
      {lane.band && (
        <>
          <path
            className="lane-band"
            d={`${linePath(lane.band.high, g.x, (v) => yOf(v, lane.band?.lo ?? 0, lane.band?.hi ?? 1, box.top, box.bottom))}${lane.band.low
              .slice()
              .reverse()
              .map(
                ([px, v]) =>
                  `L${r1(g.x(px))} ${r1(yOf(v ?? 0, lane.band?.lo ?? 0, lane.band?.hi ?? 1, box.top, box.bottom))}`,
              )
              .join('')}Z`}
          />
          {(() => {
            const band = lane.band;
            const spot = bandLabelSpot(lane, band, g, box, [
              ...avoid,
              ...words.map((w) => wordRect(w.text, w.x, w.y, w.anchor, 13)),
              ...lane.rules
                .filter((r) => r.label)
                .map((r) => {
                  const y = yOf(r.v, r.lo, r.hi, box.top, box.bottom);
                  return r.place === 'end'
                    ? wordRect(r.label, g.right + 4, y + 4, 'start')
                    : wordRect(r.label, LEFT + (g.right - LEFT) * 0.47, y - 6, 'start');
                }),
            ]);
            return (
              <text className="lane-band-label" x={r1(spot.x)} y={r1(spot.y)} textAnchor={spot.anchor}>
                {band.label}
              </text>
            );
          })()}
        </>
      )}
      {lane.rules.map((r) => {
        const y = r1(yOf(r.v, r.lo, r.hi, box.top, box.bottom));
        return (
          <g key={`rule-${r.v}`}>
            <line className="lane-rule" x1={LEFT} x2={g.right} y1={y} y2={y} />
            {r.label &&
              (r.place === 'end' ? (
                <text className="lane-rule-label" x={g.right + 4} y={y + 4}>
                  {r.label}
                </text>
              ) : (
                <text className="lane-rule-label" x={r1(LEFT + (g.right - LEFT) * 0.47)} y={y - 6}>
                  {r.label}
                </text>
              ))}
          </g>
        );
      })}
      {lane.marks.map((m) => (
        <g key={`mark-${m.x}`}>
          <line className="lane-mark" x1={r1(g.x(m.x))} x2={r1(g.x(m.x))} y1={box.top} y2={box.bottom} />
          <text className="lane-mark-label" x={r1(g.x(m.x) - 4)} y={box.y0 + 14} textAnchor="end">
            {m.label}
          </text>
        </g>
      ))}
      {lane.series.map((s) => (
        <SeriesLine key={s.key} s={s} g={g} box={box} />
      ))}
      {bars &&
        bars.items.map((b) => {
          const y = r1(yOf(b.v, bars.lo, bars.hi, box.top, box.bottom));
          return (
            <rect
              key={b.x}
              className={`lane-bar is-${b.tone}`}
              x={r1(g.x(b.x) - slot / 2)}
              width={r1(slot)}
              y={y}
              height={Math.max(0, r1(box.bottom - y))}
              rx={3}
            />
          );
        })}
      {bars?.mark &&
        (() => {
          const last = bars.items.at(-1);
          if (!last) return null;
          return (
            <text
              className="lane-val tone-text"
              x={r1(g.right + 6)}
              y={r1(yOf(last.v, bars.lo, bars.hi, box.top, box.bottom) + 4)}
            >
              {bars.mark}
            </text>
          );
        })()}
      <line className="lane-axis" x1={LEFT} x2={g.right} y1={box.bottom} y2={box.bottom} />
      {legend}
      {words.map((w) => (
        <text key={w.key} className={w.className} x={r1(w.x)} y={r1(w.y)} textAnchor={w.anchor}>
          {w.text}
        </text>
      ))}
    </g>
  );
}

export function LaneChart({
  view: given,
  info,
  onSlot,
  className,
  testId,
}: {
  view: LaneChartView;
  /** the (i) after the header's title: how to read the chart */
  info?: { text: ReactNode; label: string };
  /** a tap on a slot (the bars of the last shots) */
  onSlot?: { slots: { slot: number; label: string; id: string }[]; open: (id: string) => void };
  className?: string;
  testId?: string;
}) {
  const view = useMemo(() => fitLanes(given), [given]);
  const [ref, width] = useWidth<HTMLDivElement>(334);
  const [cursor, setCursor] = useState<ScrubReading | null>(null);
  const g = geometry(view, width);
  const first = g.lanes[0];
  const lastLane = g.lanes.at(-1);
  // the pre-infusion's name at the bottom of its band (prototype); when the band is narrower than the word, higher up,
  // where the first seconds have no curve yet
  const spanWord =
    view.span && first
      ? {
          x: Math.max(LEFT + charW(view.span.label, 12) / 2, (g.x(view.span.from) + g.x(view.span.to)) / 2),
          y:
            charW(view.span.label, 12) + 8 > g.x(view.span.to) - g.x(view.span.from)
              ? first.top + 34
              : first.bottom - 12,
        }
      : null;
  const readAt = (px: number) => {
    if (!view.at) return;
    const span = view.x.to - view.x.from || 1;
    setCursor(view.at(view.x.from + ((px - LEFT) / (g.right - LEFT)) * span));
  };
  const onPointer = (e: PointerEvent<SVGSVGElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    readAt(((e.clientX - r.left) / (r.width || 1)) * width);
  };
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (!view.at) return;
    const now = cursor?.x ?? view.x.from;
    const next =
      e.key === 'ArrowRight'
        ? now + view.step
        : e.key === 'ArrowLeft'
          ? now - view.step
          : e.key === 'Home'
            ? view.x.from
            : e.key === 'End'
              ? view.x.to
              : null;
    if (e.key === 'Escape') setCursor(null);
    if (next === null) return;
    e.preventDefault();
    setCursor(view.at(next));
  };
  const head = view.whole && (
    <div className="lanes-head" data-testid={testId ? `${testId}-head` : undefined}>
      <span className="title-info">
        <span className="lanes-title">{cursor?.title ?? view.whole.title}</span>
        {info && view.at && <InfoTip text={info.text} label={info.label} />}
      </span>
      {/* one line: the values drop out whole where the title leaves no room (WRITING.md 0a), never under it */}
      <span className="lanes-values num" aria-live="polite">
        {/* an empty first item holds the first line, so values that do not fit go to the hidden second one whole */}
        <span className="fit-spacer" aria-hidden="true" />
        <span className="lanes-values-text">{cursor ? cursor.values : view.whole.values}</span>
      </span>
    </div>
  );
  return (
    <figure className={className ? `lanes ${className}` : 'lanes'} data-testid={testId}>
      {head}
      <div ref={ref} className="lanes-plot">
        <svg
          width={width}
          height={g.height}
          viewBox={`0 0 ${width} ${g.height}`}
          role="img"
          aria-label={view.label}
          className={view.at ? 'is-readable' : undefined}
          tabIndex={view.at ? 0 : undefined}
          onPointerDown={view.at ? onPointer : undefined}
          onPointerMove={view.at ? onPointer : undefined}
          onPointerLeave={view.at ? () => setCursor(null) : undefined}
          onKeyDown={view.at ? onKey : undefined}
          onBlur={view.at ? () => setCursor(null) : undefined}
        >
          {view.span && first && lastLane && (
            <>
              <rect
                className="lane-span"
                x={r1(g.x(view.span.from))}
                width={Math.max(0, r1(g.x(view.span.to) - g.x(view.span.from)))}
                y={first.top}
                height={lastLane.bottom - first.top}
              />
              {spanWord && (
                <text className="lane-span-label" x={r1(spanWord.x)} y={spanWord.y} textAnchor="middle">
                  {view.span.label}
                </text>
              )}
            </>
          )}
          {view.lanes.map((lane, i) => (
            <LaneBody
              key={lane.key}
              lane={lane}
              g={g}
              index={i}
              width={width}
              avoid={
                i === 0 && spanWord && view.span
                  ? [wordRect(view.span.label, spanWord.x, spanWord.y, 'middle')]
                  : []
              }
            />
          ))}
          {lastLane &&
            view.x.ticks.map((t, i) => {
              const x = g.x(t.v);
              const anchor =
                i === 0 && t.v === view.x.from ? 'start' : x + charW(t.label) / 2 > width ? 'end' : 'middle';
              return (
                <text key={t.v} className="lane-tick" x={r1(x)} y={lastLane.bottom + 18} textAnchor={anchor}>
                  {t.label}
                </text>
              );
            })}
          {cursor && first && lastLane && (
            <g className="lane-cursor" aria-hidden="true">
              <line x1={r1(g.x(cursor.x))} x2={r1(g.x(cursor.x))} y1={first.top - 4} y2={lastLane.bottom} />
              {cursor.dots.map((d) => {
                const lane = view.lanes[d.lane];
                const box = g.lanes[d.lane];
                const s = lane?.series.find((x) => x.key === d.series);
                if (!s || !box) return null;
                return (
                  <circle
                    key={`${d.lane}-${d.series}`}
                    className={`lane-cursor-dot tone-${s.tone}`}
                    cx={r1(g.x(cursor.x))}
                    cy={r1(yOf(d.v, s.lo, s.hi, box.top, box.bottom))}
                    r={4.5}
                  />
                );
              })}
            </g>
          )}
          {onSlot &&
            first &&
            lastLane &&
            onSlot.slots.map((s) => {
              const w = g.x(1) - g.x(0);
              return (
                <rect
                  key={s.id}
                  className="lane-slot"
                  role="button"
                  tabIndex={0}
                  aria-label={s.label}
                  x={r1(g.x(s.slot) - w / 2)}
                  width={r1(w)}
                  y={0}
                  height={lastLane.bottom}
                  onClick={() => onSlot.open(s.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSlot.open(s.id);
                    }
                  }}
                  data-testid={testId ? `${testId}-slot-${s.slot}` : undefined}
                >
                  <title>{s.label}</title>
                </rect>
              );
            })}
        </svg>
      </div>
    </figure>
  );
}
